import React, { useEffect, useMemo } from 'react';
import { KAEUFER_BENEFIT_TO_MODAL, MODALS } from '../components/config/modals';
import useNavigationPaths from './useNavigationPaths.hook';
import { KAEUFER_PLUS_PATH, SCHUFA } from '../domain/routes';
import { useMembership } from './useMembership';
import { useGetSchufaStatus } from '../apiHooks/solvencyStatus.hook';

/**
 *
 * A hook for selecting and navigating to a modal based on the 'modal' query parameter.
 * It handles the old logic of redirecting to KaeuferPlus modals
 * For instance, it translates `/kaeuferplus?modal=OPPORTUNITY_CHECK` to the corresponding new path as defined
 * in `KAEUFER_BENEFIT_TO_MODAL`, redirecting from a deprecated modal to its modern equivalent.
 * NOTE: still used on couple of apps
 *
 * @deprecated This hook is deprecated and will be removed in future releases.
 * Use [alternative hook/method] instead for handling modal selections based on URL query parameters.
 */
export const useModalSelectionByQuery = () => {
  const { parentPath, updatePath, queryParams } = useNavigationPaths();
  const keyModal = queryParams.get('modal')?.toUpperCase() || null;

  useEffect(() => {
    const pathUpdate = keyModal
      ? KAEUFER_BENEFIT_TO_MODAL[keyModal as keyof typeof KAEUFER_BENEFIT_TO_MODAL]?.path
      : null;
    if (parentPath === KAEUFER_PLUS_PATH && keyModal && pathUpdate) {
      updatePath(`/${pathUpdate}`, 'modal');
    }
  }, [keyModal, parentPath]);
};

/**
 * Custom hook for modal selection
 * @param {string} benefitPath - The path for the benefit
 * @param {string | null} modalPath - The path for the modal
 * @returns {ModalSelection} - The modal selection
 */
export const useModalSelection = (benefitPath: string, modalPath: string | null) => {
  const availableModals = useGetAvailableModals();
  return useMemo(() => {
    const upperBenefitPath = benefitPath?.toUpperCase() as keyof typeof MODALS;
    return modalPath && availableModals?.[upperBenefitPath]?.[modalPath]
      ? availableModals[upperBenefitPath]?.[modalPath]
      : null;
  }, [benefitPath, modalPath, availableModals]);
};

export const useGetAvailableModals = () => {
  const { isMembershipError } = useMembership();
  const { isSchufaStatusError } = useGetSchufaStatus();

  const getAvailableModals = React.useCallback(
    ({ isMembershipError, isSchufaStatusError }: { isMembershipError?: boolean; isSchufaStatusError: boolean }) => {
      const modals = MODALS;

      if (isMembershipError && !!modals?.KAEUFERPLUS) {
        // @ts-expect-error KAEUFERPLUS is a required key on MODALS, but we intentionally strip it.
        delete modals.KAEUFERPLUS;
      }

      if (isSchufaStatusError && !!modals.MIETERPLUS?.[SCHUFA]) {
        delete modals.MIETERPLUS[SCHUFA];
      }

      return modals;
    },
    [],
  );

  return React.useMemo(
    () => getAvailableModals({ isMembershipError, isSchufaStatusError }),
    [getAvailableModals, isMembershipError, isSchufaStatusError],
  );
};
